import { useEffect, useState } from "react";
import sha256 from "crypto-js/sha256";
import { FaChrome, FaEdge, FaFirefoxBrowser, FaInternetExplorer, FaOpera, FaXmark } from "react-icons/fa6";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import Moment from "react-moment";
import Swal from "sweetalert2";
import { BsBrowserSafari, BsGlobeAmericas } from "react-icons/bs";
import { SiTorbrowser } from "react-icons/si";
import { DataNotFound, WaveLoader } from "../../../Hooks/PageLoader";
import { userSessionServices } from "../../../APIServices/UserData/userSessionServices";

const LinkedDevices = ({ auth }) => {
  const [sessions, setSessions] = useState([]);
  const [loader, setLoader] = useState("");
  const [logoutLoader, setLogoutLoader] = useState("");
  const [ipAddress, setIPAddress] = useState("");
  const [terminateLoader, setTerminateLoader] = useState("");

  const getDeviceFingerprint = () => {
    const fingerprintString = [
      navigator.userAgent,
      navigator.platform,
      window.screen.width,
      window.screen.height,
      window.screen.colorDepth,
      navigator.language,
      new Date().getTimezoneOffset(),
    ].join("|");

    const hash = sha256(fingerprintString).toString();
    return hash;
  };

  const getUserAllSession = async () => {
    setLoader(true);
    let data = await userSessionServices.getUserAllSession(auth, getDeviceFingerprint());
    let dataIP = await userSessionServices.getClientIp(auth);
    setIPAddress(dataIP?.ip)
    setSessions(data?.data || []);
    setLoader(false);
  };

  useEffect(() => {
    getUserAllSession();
  }, [auth]);

  const getLocation = (deviceInfo) => {
    const parts = [
      deviceInfo?.city || "",
      deviceInfo?.regionName || "",
      deviceInfo?.country || "",
      deviceInfo?.zip || "",
    ]
      .map(v => v?.toString().trim())
      .filter(v => v);
    return parts.length ? parts.join(", ") : "Unknown location";
  };

  const getBrowserIcon = (browserName) => {
    if (browserName === "Chrome") return <FaChrome />;
    if (browserName === "Edge") return <FaEdge className="text-primary" />;
    if (browserName === "Firefox") return <FaFirefoxBrowser className="text-warning" />;
    if (browserName === "Opera") return <FaOpera className="text-danger" />;
    if (browserName === "Safari") return <BsBrowserSafari className="text-primary" />;
    if (browserName === "Internet Explorer") return <FaInternetExplorer className="text-primary" />;
    if (browserName === "Tor Browser") return <SiTorbrowser />;
    return <BsGlobeAmericas />;
  };

  const forceLogoutSession = async (token) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to forcefully logout this account?",
      icon: "warning",
      showCancelButton: true,
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLogoutLoader(token)
        let userData = { token };
        let data = await userSessionServices.forceLogoutSession(auth, userData);
        if (data?.statusCode === 200) {
          getUserAllSession();
        }
        setLogoutLoader("")
      }
    });
  };

  const terminateSingleSession = async (token) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to terminate this account?",
      icon: "warning",
      showCancelButton: true,
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, terminate it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setTerminateLoader(token)
        let userData = { token };
        let data = await userSessionServices.terminateSingleSession(auth, userData);
        if (data?.statusCode === 200) {
          getUserAllSession();
        }
        setTerminateLoader("")
      }
    });
  };

  const forceLogoutAllOtherSessions = async () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to forcefully logout all other account?",
      icon: "warning",
      showCancelButton: true,
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLogoutLoader("all")
        let userData = {};
        let data = await userSessionServices.forceLogoutAllOtherSessions(auth, userData);
        if (data?.statusCode === 200) {
          getUserAllSession();
        }
        setLogoutLoader("")
      }
    });
  };

  const terminateAllOtherSessions = async () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to terminate all other account?",
      icon: "warning",
      showCancelButton: true,
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, terminate it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setTerminateLoader("all")
        let userData = {};
        let data = await userSessionServices.terminateAllOtherSessions(auth, userData);
        if (data?.statusCode === 200) {
          getUserAllSession();
        }
        setTerminateLoader("")
      }
    });
  };
  return (
    <>
      <div className="w-75 m-auto pb-1 pt-3">
        <h5 className="fw-bold mb-1">Linked to Your Account</h5>
      </div>
      <div className="overflow-auto" style={{ height: "calc(100vh - 48px)" }}>
        {loader ? <WaveLoader /> : sessions?.length === 0 ? <DataNotFound msg={`Link device not found.`} /> :
          <div className="w-75 m-auto py-3">
            <Card className="border rounded-1 mb-3">
              <Card.Body>
                {sessions?.length > 1 && (
                  <div
                    style={{ lineHeight: 1 }}
                    className="hstack gap-2 justify-content-end mb-3"
                  >
                    <Button
                      variant="outline-danger"
                      className="rounded-1 text-nowrap py-1 px-2"
                      style={{ fontSize: 11 }}
                      disabled={logoutLoader === "all"}
                      onClick={forceLogoutAllOtherSessions}
                    >
                      Logout All Other Devices Forcefully
                    </Button>
                    <Button
                      variant="outline-danger"
                      className="rounded-1 text-nowrap py-1 px-2"
                      disabled={terminateLoader === "all"}
                      style={{ fontSize: 11 }}
                      onClick={terminateAllOtherSessions}
                    >
                      Terminate All Other Device
                    </Button>
                  </div>
                )}
                <Container fluid>
                  {sessions?.map((item, index) => {
                    const deviceInfo = item?.deviceInfo || {};
                    const browser = deviceInfo?.browser || {};
                    const os = deviceInfo?.os || {};
                    const device = deviceInfo?.device || {};

                    return (
                      <Row className="my-3 align-items-center" key={item._id || index}>
                        <Form.Group as={Col} md={5}>
                          <div className="d-flex gap-2 align-items-start">
                            <span style={{ fontSize: 24 }} className="text-muted">
                              {getBrowserIcon(browser?.name)}
                            </span>
                            <div style={{ lineHeight: 1.3 }}>
                              <h6 className="mb-1 fw-bold" style={{ fontSize: 14 }}>
                                {browser?.name || "Unknown Browser"}
                                {browser?.version && (<span style={{ fontSize: 11 }}> v.{browser?.version}</span>)}
                              </h6>
                              <Form.Label className="m-0 d-block" style={{ fontSize: 11 }}>
                                {os?.name || "Unknown OS"} {os?.version}{" "}
                                <span className="text-capitalize">
                                  ({device?.type || "device"})
                                </span>
                              </Form.Label>
                              <Form.Label className="mb-0" style={{ fontSize: 11 }}>
                                Last access:{" "}
                                <Moment format="DD MMM YYYY | HH:mm" withTitle>
                                  {item?.lastActiveAt}
                                </Moment>
                              </Form.Label>
                            </div>
                          </div>
                        </Form.Group>
                        <Form.Group as={Col} md={3}>
                          <div style={{ lineHeight: 1.3 }}>
                            <Form.Label className="m-0 d-block" style={{ fontSize: 11 }}>
                              IP: {deviceInfo?.ipAddress || "N/A"}
                            </Form.Label>
                            <Form.Label className="mb-0" style={{ fontSize: 11 }}>
                              {getLocation(deviceInfo)}
                            </Form.Label>
                            <Form.Label className="mb-0" style={{ fontSize: 11 }}>
                              Timezone: {deviceInfo?.timezone || "Unknown"}
                            </Form.Label>
                          </div>
                        </Form.Group>
                        <Form.Group as={Col} md={4}>
                          <div className="hstack gap-2 justify-content-end">
                            {!(item?.deviceFingerprint === getDeviceFingerprint() && ipAddress === deviceInfo?.ipAddress) ? (
                              <>
                                <Button
                                  variant={
                                    item?.isActive
                                      ? "outline-danger"
                                      : "outline-secondary"
                                  }
                                  className="rounded-1 text-nowrap py-1 px-2"
                                  style={{ fontSize: 11 }}
                                  disabled={!item?.isActive || logoutLoader === item?.token}
                                  onClick={() => forceLogoutSession(item?.token)}
                                >
                                  Force Logout
                                </Button>

                                <Button
                                  variant="outline-danger"
                                  className="rounded-1 text-nowrap py-1 px-2"
                                  style={{ fontSize: 11 }}
                                  disabled={terminateLoader === item?.token}
                                  onClick={() =>
                                    terminateSingleSession(item?.token)
                                  }
                                >
                                  Terminate Device
                                </Button>
                              </>
                            ) : ((item?.deviceFingerprint === getDeviceFingerprint() && ipAddress === deviceInfo?.ipAddress) && (
                              <h6 className="m-0 fw-bold text-success" style={{ fontSize: 13 }}>
                                Your current device
                              </h6>
                            )
                            )}
                          </div>
                        </Form.Group>
                      </Row>
                    );
                  })}
                </Container>
              </Card.Body>
            </Card>
          </div>
        }
      </div>
    </>
  );
};

export default LinkedDevices;
